import type { CSSProperties } from 'react';
import { devClubMarkSrc } from '@/lib/devclub-verde';
import styles from './BrandMark.module.css';

export type EcosystemBrand = 'devclub' | 'iaclub' | 'works';

const labels: Record<EcosystemBrand, string> = {
  devclub: 'DevClub',
  iaclub: 'IAClub',
  works: 'Works AI',
};

const WORKS_SRC = {
  full: '/brand/logos/works-ai.svg',
  symbol: '/brand/logos/works-ai-simbolo.svg',
} as const;

/**
 * Marca oficial de cada ecossistema, sem redesenho. DevClub e Works AI são
 * SVGs de fundo transparente (public/brand/logos); o IAClub ainda é um recorte
 * do PNG oficial. `variant="symbol"` mostra só o símbolo do Works AI, para
 * espaços baixos em que o nome já aparece ao lado.
 */
export function BrandMark({
  brand,
  variant = 'full',
  className,
  style,
  decorative = false,
}: {
  brand: EcosystemBrand;
  variant?: 'full' | 'symbol';
  className?: string;
  style?: CSSProperties;
  decorative?: boolean;
}) {
  return (
    <span
      className={`${styles.mark} ${styles[brand]} ${className || ''}`}
      style={style}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : labels[brand]}
      aria-hidden={decorative || undefined}
      data-variant={brand === 'works' ? variant : undefined}
    >
      {brand === 'iaclub' ? (
        <svg
          viewBox="570 129 1360 1269"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <image href="/brand/logos/ia-club.png" width={2500} height={1528} />
        </svg>
      ) : (
        <img
          src={brand === 'devclub' ? devClubMarkSrc() : WORKS_SRC[variant]}
          alt=""
          draggable={false}
        />
      )}
    </span>
  );
}

/** Shared signature for platform-wide banners; individual ecosystems keep their own mark. */
export function BrandRail({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`${styles.rail} ${className || ''}`}
      style={style}
      aria-label="Marcas da plataforma"
    >
      {(['devclub', 'iaclub', 'works'] as const).map((brand) => (
        <figure className={styles.item} key={brand}>
          <BrandMark brand={brand} variant="symbol" decorative />
          <figcaption>{labels[brand]}</figcaption>
        </figure>
      ))}
    </div>
  );
}
